import type { LabelColor } from "components/Label";
import type {
  REST_schema,
  WhatsappCampaign,
  WhatsappCampaignResponseReplySource,
  WhatsappCampaignStatus,
  WhatsappRecipientState,
  WhatsappSkipReason
} from "../../../../@types/sharedSchema";
import { WHATSAPP_MIN_RECENCY_MINUTES } from "../../../../@types/sharedSchema";

/**
 * Hebrew display metadata for the campaigns screens — statuses, recipient states and skip
 * reasons, in ONE place so the list, the detail dialog and the wizard never drift apart.
 */

export const CAMPAIGN_STATUS_LABELS: Record<
  WhatsappCampaignStatus,
  { label: string; color: LabelColor }
> = {
  draft: { label: "טיוטה", color: "default" },
  scheduled: { label: "מתוזמן", color: "default" },
  running: { label: "פעיל", color: "info" },
  paused_by_user: { label: "מושהה", color: "warning" },
  paused_by_system: { label: "הושהה אוטומטית", color: "error" },
  completed: { label: "הושלם", color: "success" },
  cancelled: { label: "בוטל", color: "default" },
  failed: { label: "נכשל", color: "error" }
};

export const RECIPIENT_STATE_LABELS: Record<
  WhatsappRecipientState,
  { label: string; color: LabelColor }
> = {
  queued: { label: "בתור", color: "default" },
  sending: { label: "בשליחה", color: "info" },
  sent: { label: "נשלחה", color: "info" },
  delivered: { label: "נמסרה", color: "success" },
  read: { label: "נקראה", color: "success" },
  failed: { label: "נכשלה", color: "error" },
  skipped: { label: "דולגה", color: "warning" }
};

export const SKIP_REASON_LABELS: Record<WhatsappSkipReason, string> = {
  opted_out: "ביקש להסיר",
  suppressed: "ברשימה השחורה",
  recent_bulk: "קיבל הודעה לאחרונה",
  unreachable: "לא מחובר לוואטסאפ",
  marketing_capped: "מגבלת תדירות של Meta",
  invalid_phone: "מספר לא תקין",
  landline: "קו נייח",
  self: "המספר שלך",
  duplicate: "כפול",
  experiment_holdout: "קבוצת ביקורת",
  daily_budget: "חריגה מהמכסה היומית",
  tenant_not_ready: "החיבור לא פעיל",
  kill_switch: "שליחה מושבתת"
};

/** A skip reason coming off the wire as a plain string (e.g. `skippedAtCreate`). */
export const skipReasonToHebrew = (reason: string): string =>
  (SKIP_REASON_LABELS as Record<string, string>)[reason] ?? reason;

export const RETRY_REFUSED_LABELS: Record<
  "permanent_error" | "attempts_exhausted" | "not_failed",
  string
> = {
  permanent_error: "שגיאה סופית — אי אפשר לשלוח שוב",
  attempts_exhausted: "מוצו הניסיונות",
  not_failed: "לא במצב נכשל"
};

/**
 * Dates arrive in three shapes: Firestore Timestamp (from `whatsappGetCampaign`), ISO string
 * (list/recipients wire types) and occasionally a real Date. Normalize before formatting.
 */
export const toDateSafe = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  const maybeTimestamp = value as { toDate?: () => Date };
  if (typeof maybeTimestamp.toDate === "function") return maybeTimestamp.toDate();
  return undefined;
};

export const formatDateTimeHe = (value: unknown): string => {
  const date = toDateSafe(value);
  if (!date) return "—";
  return date.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

/**
 * "לפני 5 דקות" for the recent, a date for the rest — response cards are read as a feed, and a
 * timestamp from three minutes ago means more as "just now" than as 14:32.
 */
export const formatRelativeTimeHe = (value: unknown, now: Date = new Date()): string => {
  const date = toDateSafe(value);
  if (!date) return "—";
  const minutes = Math.round((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "עכשיו";
  if (minutes < 60) return `לפני ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "לפני שעה" : `לפני ${hours} שעות`;
  const days = Math.floor(hours / 24);
  if (days === 1) return `אתמול ${date.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}`;
  if (days < 7) return `לפני ${days} ימים`;
  return formatDateTimeHe(date);
};

// ─── Pacing ───────────────────────────────────────────────────────────────────────────────

/**
 * Gap options, seconds — the SAME table in the wizard, the preview gate and the control room.
 * The server floors at its own engine minimum (20s, jittered to 80s) and only ever lets a
 * realtor slow a campaign down; the first option is that floor.
 */
export const GAP_OPTIONS: { seconds: number; label: string }[] = [
  { seconds: 20, label: "קצב רגיל (ברירת מחדל)" },
  { seconds: 60, label: "דקה" },
  { seconds: 120, label: "2 דקות" },
  { seconds: 300, label: "5 דקות" },
  { seconds: 600, label: "10 דקות" }
];

/** The hours a send window may start or end at — 09:00..20:00 Asia/Jerusalem, the engine's own bounds. */
export const SEND_WINDOW_HOURS = Array.from({ length: 12 }, (_, i) => 9 + i);

/**
 * "Skip whoever I messaged in the last…" — the SAME choices in every send dialog (`RecencySkipControl`).
 * The first is the engine's floor and means "skip nobody" (only a message minutes old is a double
 * send); it is the DEFAULT since 2026-10-21 — Haim: a realtor who picked these people means to reach
 * them. It used to be 30 days, which skipped a test campaign's only recipient.
 */
export const RECENCY_OPTIONS: { minutes: number; label: string; phrase: string }[] = [
  { minutes: WHATSAPP_MIN_RECENCY_MINUTES, label: "10 דקות", phrase: "ב-10 הדקות האחרונות" },
  { minutes: 1440, label: "24 שעות", phrase: "ב-24 השעות האחרונות" },
  { minutes: 10080, label: "7 ימים", phrase: "ב-7 הימים האחרונים" },
  { minutes: 43200, label: "30 יום", phrase: "ב-30 הימים האחרונים" }
];

/** Skip nobody — what every dialog starts with. */
export const DEFAULT_RECENCY_MINUTES = WHATSAPP_MIN_RECENCY_MINUTES;

/** The window the switch turns on with — the extension's own "30 days", and the old default. */
export const RECENCY_SKIP_ON_MINUTES = 43200;

export const isSkippingRecent = (minutes: number): boolean => minutes > WHATSAPP_MIN_RECENCY_MINUTES;

/** One line for a summary: who gets the message under this window. */
export const describeRecencySkipHe = (minutes: number): string =>
  isSkippingRecent(minutes)
    ? `בלי מי שקיבל ממך הודעה ${RECENCY_OPTIONS.find((option) => option.minutes === minutes)?.phrase ?? "לאחרונה"}`
    : "גם למי שקיבל ממך הודעה לאחרונה";

export const formatHourHe = (hour: number): string => `${String(hour).padStart(2, "0")}:00`;

/** The option that matches a stored pacing (its minimum gap); the floor when nothing matches. */
export const gapOptionForPacing = (pacing: Pick<WhatsappCampaign["pacing"], "minGapMs"> | undefined): number => {
  const seconds = Math.round((pacing?.minGapMs ?? 20_000) / 1000);
  return GAP_OPTIONS.find((option) => option.seconds === seconds)?.seconds ?? GAP_OPTIONS[0].seconds;
};

/** "המתנה של 20–80 שניות בין הודעות · שליחה בין 09:00 ל-20:00" — the stored pacing, in words. */
export const describePacingHe = (pacing: WhatsappCampaign["pacing"] | undefined): string => {
  if (!pacing) return "קצב ברירת המחדל";
  const minSeconds = Math.round(pacing.minGapMs / 1000);
  const maxSeconds = Math.round(pacing.maxGapMs / 1000);
  const gap =
    minSeconds === maxSeconds
      ? minSeconds >= 60 && minSeconds % 60 === 0
        ? `${minSeconds / 60} דקות`
        : `${minSeconds} שניות`
      : `${minSeconds}–${maxSeconds} שניות`;
  return `המתנה של ${gap} בין הודעות · שליחה בין ${formatHourHe(pacing.sendWindow.startHour)} ל-${formatHourHe(pacing.sendWindow.endHour)}`;
};

// ─── Responses ────────────────────────────────────────────────────────────────────────────

export const REPLY_SOURCE_LABELS: Record<WhatsappCampaignResponseReplySource, string> = {
  inbox: "מעמוד ההודעות",
  phone: "מהטלפון",
  campaign: "מכאן",
  other: "אוטומטית"
};

/** What a non-text response reads as on a card. */
export const RESPONSE_MEDIA_LABELS: Record<string, string> = {
  image: "תמונה",
  video: "סרטון",
  audio: "הודעה קולית",
  voice: "הודעה קולית",
  document: "קובץ",
  sticker: "סטיקר",
  location: "מיקום",
  contacts: "איש קשר",
  interactive: "בחירה מהתפריט",
  button: "לחיצה על כפתור",
  reaction: "תגובת אימוג'י"
};

/** "בעוד 3 שעות" / "בעוד 40 דקות" for a deadline still ahead; null once it passed (or is unknown). */
export const formatTimeLeftHe = (until: unknown, now: Date = new Date()): string | null => {
  const date = toDateSafe(until);
  if (!date) return null;
  const minutes = Math.round((date.getTime() - now.getTime()) / 60_000);
  if (minutes <= 0) return null;
  if (minutes === 1) return "בעוד דקה";
  if (minutes < 60) return `בעוד ${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours === 1) return "בעוד שעה";
  if (hours === 2) return "בעוד שעתיים";
  // A whole day ahead is still "in 24 hours" — the 24h window is exactly that long.
  if (hours <= 24) return `בעוד ${hours} שעות`;
  return `ב-${formatDateTimeHe(date)}`;
};

/** How a stored message reads in one line: "[תמונה] caption" / the text / "—". */
export const responseMessageText = (message: { type: string; text?: string; caption?: string }): string => {
  const media = message.type !== "text" ? RESPONSE_MEDIA_LABELS[message.type] ?? message.type : null;
  return [media ? `[${media}]` : null, message.text ?? message.caption ?? null].filter(Boolean).join(" ") || "—";
};

// ─── The people list (delivery + response, one row per person) ─────────────────────────────

export type ResponseStatus = NonNullable<REST_schema.WhatsappCampaignRecipientView["response"]>["status"];

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, { label: string; color: LabelColor }> = {
  awaiting: { label: "ממתין למענה", color: "warning" },
  replied: { label: "נענה", color: "success" }
};

/**
 * The filter chips. The three mid-funnel delivery filters name their EXCLUSIVE bucket ("sent and
 * not yet delivered") because the tiles above them show the cumulative funnel under the same
 * words — a chip "נשלחו · 20" beside a tile "נשלחו 100" needs to say which 20 it means.
 */
export const PEOPLE_FILTER_LABELS: Record<REST_schema.WhatsappCampaignPeopleFilter, string> = {
  all: "הכל",
  responded: "הגיבו",
  awaiting: "ממתינות למענה",
  replied: "נענו",
  opted_out: "ביקשו הסרה",
  read: "נקראו",
  delivered: "נמסרו (טרם נקראו)",
  sent: "נשלחו (טרם נמסרו)",
  failed: "נכשלו",
  skipped: "דולגו",
  queued: "בתור"
};

/** What a row says about someone who asked to be removed after the campaign reached them. */
export const OPTED_OUT_ROW_LABEL = "ביקש הסרה";

/** The chips' order: response filters first (the reason the page is opened), then the unsubscribes, the funnel top-down, and the leftovers. */
export const PEOPLE_FILTER_ORDER: REST_schema.WhatsappCampaignPeopleFilter[] = [
  "all",
  "responded",
  "awaiting",
  "replied",
  "opted_out",
  "read",
  "delivered",
  "sent",
  "failed",
  "skipped",
  "queued"
];

export const PEOPLE_SORT_LABELS: Record<REST_schema.WhatsappCampaignPeopleSort, string> = {
  activity: "תגובות אחרונות קודם",
  phone: "לפי מספר טלפון"
};

/** What an empty view says, per filter — a filter that matches nobody is an answer, not a blank. */
export const PEOPLE_EMPTY_LABELS: Record<REST_schema.WhatsappCampaignPeopleFilter, string> = {
  all: "אין נמענים להצגה",
  responded: "עדיין אין תגובות לקמפיין",
  awaiting: "אין תגובות שממתינות למענה",
  replied: "עדיין לא נענו תגובות",
  opted_out: "אף נמען לא ביקש הסרה",
  read: "עדיין לא נקראו הודעות",
  delivered: "אין הודעות שנמסרו וטרם נקראו",
  sent: "אין הודעות שנשלחו וטרם נמסרו",
  failed: "אין הודעות שנכשלו",
  skipped: "אף נמען לא דולג",
  queued: "אין נמענים בתור"
};

/**
 * The live count behind each chip, off the campaign doc. Delivery counts are the EXCLUSIVE
 * buckets (the state machine moves a recipient between them), which is what the matching filter
 * returns; the response counts fall back to the older `totals.replied` for campaigns that predate
 * `responseStats`. Each `responseStats` counter is absent until its own first event, so every one
 * is read with a fallback. `opted_out` is its own bucket — an unsubscribe is not a response.
 */
export const peopleFilterCounts = (
  totals: WhatsappCampaign["totals"],
  responseStats: WhatsappCampaign["responseStats"] | undefined
): Record<REST_schema.WhatsappCampaignPeopleFilter, number> => {
  const responders = responseStats?.responders ?? totals.replied ?? 0;
  const awaiting = responseStats?.awaiting ?? 0;
  return {
    all: totals.selected,
    responded: responders,
    awaiting,
    replied: Math.max(0, responders - awaiting),
    opted_out: responseStats?.optedOut ?? 0,
    read: totals.read,
    delivered: totals.delivered,
    sent: totals.sent,
    failed: totals.failed,
    skipped: totals.skipped,
    queued: totals.queued
  };
};

/**
 * The delivery FUNNEL: each step includes the ones after it, so "נשלחו" is everything that left
 * our hands. The raw buckets are exclusive — a campaign whose one message was read holds
 * `sent: 0, read: 1` — and read as "sent 0" to a realtor, which is wrong twice over.
 */
export const campaignDeliveryFunnel = (totals: WhatsappCampaign["totals"]) => ({
  sent: totals.sent + totals.delivered + totals.read,
  delivered: totals.delivered + totals.read,
  read: totals.read
});

/** The skip reason or the error, in Hebrew — empty when the row has neither. */
export const recipientDetailText = (
  recipient: Pick<REST_schema.WhatsappCampaignRecipientView, "skipReason" | "errorTitle" | "errorCode" | "state" | "attempts">
): string => {
  if (recipient.skipReason) return SKIP_REASON_LABELS[recipient.skipReason] ?? recipient.skipReason;
  if (recipient.errorTitle || recipient.errorCode) {
    const title = recipient.errorTitle ?? "שגיאה";
    const withCode = recipient.errorCode ? `${title} (${recipient.errorCode})` : title;
    return recipient.state === "failed" && recipient.attempts > 1 ? `${withCode} · ${recipient.attempts} ניסיונות` : withCode;
  }
  return "";
};

/** "נשלחה 08/10/2026, 14:32" lines — the read time outranks the delivered one (a read message was delivered). */
export const recipientTimeLines = (
  recipient: Pick<REST_schema.WhatsappCampaignRecipientView, "sentAt" | "deliveredAt" | "readAt" | "failedAt">
): string[] => {
  const lines: string[] = [];
  if (recipient.sentAt) lines.push(`נשלחה ${formatDateTimeHe(recipient.sentAt)}`);
  if (recipient.readAt) lines.push(`נקראה ${formatDateTimeHe(recipient.readAt)}`);
  else if (recipient.deliveredAt) lines.push(`נמסרה ${formatDateTimeHe(recipient.deliveredAt)}`);
  if (recipient.failedAt) lines.push(`נכשלה ${formatDateTimeHe(recipient.failedAt)}`);
  return lines;
};

/** The creator's name as the campaign screens print it: the directory's, the snapshot's, or the email's local part. */
export const campaignCreatorName = (userId: string | undefined, snapshotName?: string | null, directoryName?: string | null): string =>
  directoryName?.trim() || snapshotName?.trim() || userId?.split("@")[0] || "—";

/**
 * Wizard textarea line → E.164. Any format goes: strip to digits, `05X…` becomes `+972…`,
 * anything else keeps its digits behind a `+`. Too-short lines are rejected (returns null).
 */
export const normalizePhoneLineToE164 = (line: string): string | null => {
  const digits = line.replace(/\D/g, "");
  if (digits.length < 8) return null;
  if (digits.startsWith("05") && digits.length === 10) return `+972${digits.slice(1)}`;
  return `+${digits}`;
};
